import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    EnvironmentHealth,
    Finding,
    FindingAction,
    HealthSeverity,
    WORKER_LABEL,
    WorkerStatus,
} from '../../api/health';
import { formatAge, formatCount, formatDuration } from '../../api/queueMonitor';
import { WorkerActivityMonitor } from '../../hooks/useWorkerActivity';
import './Health.scss';

export interface IHealthPanelProps {
    health: EnvironmentHealth | null;
    loading: boolean;
    error: string | null;
    onReload: () => void;
    /**
     * Runs one of the offered actions. The panel works out what should be done; carrying it
     * out belongs with the page that owns the queues and the error log.
     */
    onAction: (finding: Finding, action: FindingAction) => void;
    /** Which action is running right now, as "<finding code>:<action kind>". */
    busyAction?: string | null;
    /**
     * The telemetry of one app, opened on demand.
     *
     * Held outside this component because a finding's "show me what this app did" action and
     * a click on the chip itself have to open the same panel; two pieces of state would let
     * them disagree. The panel it drives is drawn by the page, next to the queue badges that
     * open it - so this is only the chips knowing which of them is the open one.
     */
    activity: WorkerActivityMonitor;
}

const SEVERITY_ICON: Record<HealthSeverity, IconProp> = {
    Critical: 'circle-xmark',
    Warning: 'triangle-exclamation',
    Info: 'circle-info',
    Healthy: 'check',
};

/**
 * What is wrong with this environment, and what to do about it.
 *
 * This panel exists because the numbers alone were not enough. A queue with eighty-five
 * messages on it, an app that may or may not be running and an error log full of yesterday
 * are three readings that only mean something together, and putting them together was left
 * to whoever was looking - every single time, from memory, against a runbook in another
 * window.
 *
 * So the panel does not show measurements. It shows conclusions, each with the measurement
 * it rests on folded up underneath, and next to it the buttons that act on it. Nothing is
 * carried out on its own: the dashboard works out what is needed, a person decides whether
 * it happens.
 */
export const HealthPanel = ({
    health,
    loading,
    error,
    onReload,
    onAction,
    busyAction,
    activity,
}: IHealthPanelProps) => {
    /** Which findings have their evidence unfolded. */
    const [openEvidence, setOpenEvidence] = useState<Set<string>>(new Set());

    const toggleEvidence = (code: string) =>
        setOpenEvidence((previous) => {
            const next = new Set(previous);

            if (next.has(code)) {
                next.delete(code);
            } else {
                next.add(code);
            }

            return next;
        });

    const workers = useMemo(
        () => health?.workers?.workers ?? [],
        [health?.workers?.workers]
    );

    if (loading && !health) {
        return (
            <section className="health health--loading" aria-busy="true">
                <FontAwesomeIcon icon="spinner" spin className="me-2" />
                Bezig met vaststellen wat er in deze omgeving gebeurt.
            </section>
        );
    }

    if (!health) {
        return error ? (
            <section className="health health--broken" role="alert">
                <FontAwesomeIcon icon="circle-xmark" className="me-2" />
                De staat van deze omgeving kon niet worden opgehaald. {error}
                <button
                    type="button"
                    className="health__reload"
                    onClick={onReload}
                >
                    <FontAwesomeIcon icon="rotate" />
                    Opnieuw
                </button>
            </section>
        ) : null;
    }

    const severity = health.severity;
    const healthy = severity === 'Healthy';

    return (
        <section
            className={`health health--${severity.toLowerCase()}`}
            aria-live="polite"
        >
            <header className="health__head">
                <FontAwesomeIcon
                    icon={SEVERITY_ICON[severity]}
                    className="health__icon"
                />

                <div className="health__headline">
                    <h3 className="health__summary">{health.summary}</h3>

                    <p className="health__sampled">
                        {`Vastgesteld ${
                            formatAge(health.sampledAt) ?? 'net'
                        } geleden`}
                        {error && ` · laatste poging mislukte: ${error}`}
                    </p>
                </div>

                <button
                    type="button"
                    className="health__reload"
                    onClick={onReload}
                    title="Nu opnieuw vaststellen"
                >
                    <FontAwesomeIcon icon="rotate" spin={loading} />
                </button>
            </header>

            <WorkerStrip
                workers={workers}
                error={health.workers?.error}
                openApp={activity.appKey}
                onOpen={activity.open}
            />

            {healthy ? (
                <p className="health__all-clear">
                    Er staat niets te wachten, niets loopt stuk en alle apps die
                    een spoor achterlaten leven. Verschijnt hier iets, dan staat
                    er meteen bij wat je eraan doet.
                </p>
            ) : (
                <ol className="health__findings">
                    {health.findings.map((finding) => (
                        <FindingRow
                            key={`${finding.code}:${finding.queue ?? ''}:${
                                finding.worker ?? ''
                            }`}
                            finding={finding}
                            open={openEvidence.has(evidenceKey(finding))}
                            onToggleEvidence={() =>
                                toggleEvidence(evidenceKey(finding))
                            }
                            onAction={onAction}
                            busyAction={busyAction}
                        />
                    ))}
                </ol>
            )}
        </section>
    );
};

/** A finding is identified by its rule plus what it is about; the code alone repeats. */
const evidenceKey = (finding: Finding) =>
    `${finding.code}:${finding.queue ?? ''}:${finding.worker ?? ''}`;

interface IFindingRowProps {
    finding: Finding;
    open: boolean;
    onToggleEvidence: () => void;
    onAction: (finding: Finding, action: FindingAction) => void;
    busyAction?: string | null;
}

const FindingRow = ({
    finding,
    open,
    onToggleEvidence,
    onAction,
    busyAction,
}: IFindingRowProps) => (
    <li
        className={`finding finding--${finding.severity.toLowerCase()}${
            finding.blocksOtherWork ? ' finding--blocking' : ''
        }`}
    >
        <div className="finding__head">
            <FontAwesomeIcon
                icon={SEVERITY_ICON[finding.severity]}
                className="finding__icon"
            />

            <h4 className="finding__title">{finding.title}</h4>

            {finding.count !== null && finding.count !== undefined && (
                <span className="finding__count">
                    {formatCount(finding.count)}
                </span>
            )}
        </div>

        <p className="finding__explanation">{finding.explanation}</p>

        {finding.blocksOtherWork && (
            <p className="finding__blocking">
                Los dit eerst op. Zolang dit staat, is al het andere in deze
                omgeving hier gevolg van.
            </p>
        )}

        <button
            type="button"
            className="finding__evidence-toggle"
            onClick={onToggleEvidence}
            aria-expanded={open}
        >
            <FontAwesomeIcon icon={open ? 'angle-down' : 'angle-right'} />
            Waarop dit berust
        </button>

        {open && (
            <p className="finding__evidence">
                {finding.evidence}
                {finding.runbookSection && (
                    <span className="finding__runbook">
                        {` Runbook §${finding.runbookSection}.`}
                    </span>
                )}
            </p>
        )}

        {finding.actions.length > 0 && (
            <div className="finding__actions">
                {finding.actions.map((action) => {
                    const key = `${finding.code}:${action.kind}`;
                    const busy = busyAction === key;

                    return (
                        <div className="finding__action" key={key}>
                            <button
                                type="button"
                                className={`finding__button${
                                    action.mutates
                                        ? ' finding__button--mutates'
                                        : ''
                                }`}
                                onClick={() => onAction(finding, action)}
                                disabled={busy}
                                title={action.description}
                            >
                                {busy && (
                                    <FontAwesomeIcon icon="spinner" spin />
                                )}
                                {action.label}
                            </button>

                            <span className="finding__action-hint">
                                {action.description}
                            </span>

                            {action.caveat && (
                                <span className="finding__caveat">
                                    <FontAwesomeIcon icon="triangle-exclamation" />
                                    {action.caveat}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        )}
    </li>
);

interface IWorkerStripProps {
    workers: WorkerStatus[];
    error?: string | null;
    /** Which app's telemetry is open, so its chip can show as pressed. */
    openApp?: string | null;
    onOpen: (appKey: string) => void;
}

/**
 * The function apps, each with what can actually be proven about it.
 *
 * The point of this row is that it is not a label saying "busy". Every chip rests on
 * something in storage: a lease a running host renews every half minute, or a timer that
 * recorded when it last fired. Where there is nothing to rest on, the chip says so instead
 * of guessing - an app without a timer leaves no trace, and calling that "stopped" would be
 * the same mistake this dashboard just stopped making.
 */
const WorkerStrip = ({
    workers,
    error,
    openApp,
    onOpen,
}: IWorkerStripProps) => {
    if (error) {
        return (
            <p className="workers workers--broken">
                <FontAwesomeIcon icon="triangle-exclamation" className="me-2" />
                {error}
            </p>
        );
    }

    if (workers.length === 0) {
        return null;
    }

    return (
        <ul className="workers">
            {workers.map((worker) => {
                const { label, tone } = WORKER_LABEL[worker.verdict];

                const seen =
                    worker.verdict === 'Running'
                        ? 'nu'
                        : worker.lastSeenAt
                        ? `${formatAge(worker.lastSeenAt)} geleden`
                        : null;

                const quiet =
                    worker.verdict === 'Silent' && worker.quietForSeconds
                        ? `stil sinds ${formatDuration(
                              worker.quietForSeconds
                          )}`
                        : null;

                return (
                    <li key={worker.key}>
                        <button
                            type="button"
                            className={`workers__item workers__item--${tone}${
                                openApp === worker.key
                                    ? ' workers__item--open'
                                    : ''
                            }`}
                            onClick={() => onOpen(worker.key)}
                            aria-pressed={openApp === worker.key}
                            title={`${worker.evidence}\n\nKlik om te zien wat deze app heeft gedaan.`}
                        >
                            <span className="workers__name">
                                {worker.displayName}
                            </span>

                            <span className="workers__verdict">
                                {quiet ?? label}
                            </span>

                            {seen && !quiet && (
                                <span className="workers__seen">{seen}</span>
                            )}
                        </button>
                    </li>
                );
            })}
        </ul>
    );
};
